/**
 * CartPanel — right-hand cart surface for the desktop billing screen.
 *
 * Extracted from the monolithic BillingScreen so it can host the richer
 * Phase-4 feature set without bloating the parent:
 *
 *   – Customer attach (Walk-in by default; click to open picker)
 *   – Parked-carts dropdown (hold the active sale, recall later)
 *   – Per-line price + discount editor in a Popover (no extra screen)
 *   – Per-line qty stepper, per-line remove
 *   – Inter-state (IGST) toggle
 *   – Global discount, totals, Charge button
 *
 * Keyboard:
 *   F2 → open customer picker          (when cart pane is mounted)
 *   F4 → park current cart              (when items > 0)
 *
 * Cart math is in `useCartStore`; this component is purely presentational.
 */

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Q } from '@nozbe/watermelondb';
import * as Popover from '@radix-ui/react-popover';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import {
  Trash2,
  Minus,
  Plus,
  X,
  User,
  UserPlus,
  PauseCircle,
  History,
  ChevronDown,
} from 'lucide-react';
import { useCartStore, type CartItem, getLoyaltyConfig } from '@/store/useCartStore';
import { useAppStore } from '@/store/useAppStore';
import { loadStoreGstIdentity } from '@/gst/storeIdentity';
import { resolveInterState } from '@/utils/placeOfSupply';
import { database } from '@/db';
import { Customer } from '@/db/models/Customers';
import { Invoice, LineItem } from '@/db/models/Billing';
import { Product } from '@/db/models/Inventory';
import { validatePromoCode } from '@/billing/promotionService';
import { toRupees, loyaltyRedemptionValue, maxRedeemablePoints } from '@/billing/billingEngine';
import { toBillingProduct } from '@/billing/toBillingProduct';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { NumberField, useNumberDraft } from '@/components/ui/NumberField';
import { Dialog, DialogContent } from '@/components/ui/Dialog';
import { cn } from '@/utils/cn';
import { formatCurrency } from '@/utils/format';
import { Money, Qty, Code } from '@/components/ui/numeric';
import { Explain, ExplainRow } from '@/components/ui/Explain';

interface Props {
  onCharge: () => void;
}

/** A single line crossing this quantity prompts a confirm — guards against a
 *  held "+" key or a mis-scan silently inflating the bill. */
const HIGH_QTY_THRESHOLD = 20;

export function CartPanel({ onCharge }: Props) {
  const items = useCartStore((s) => s.items);
  const totals = useCartStore((s) => s.totals);
  const itemCount = useCartStore((s) => s.itemCount);
  const isInterState = useCartStore((s) => s.isInterState);

  /**
   * Tax grouped by rate slab.
   *
   * `InvoiceTotals` carries one combined CGST/SGST figure, which is the right
   * thing to print on the bill and the wrong thing to reconcile with: GSTR-1
   * is filed per rate. Deriving it here keeps the engine's contract untouched
   * and puts the per-slab split behind the totals row, which is where someone
   * checking a return would look for it.
   */
  const taxSlabs = useMemo(() => {
    const by = new Map<number, { taxablePaisa: number; taxPaisa: number }>();
    for (const it of items) {
      const rate = it.product.gstRate;
      if (!rate) continue;
      const cur = by.get(rate) ?? { taxablePaisa: 0, taxPaisa: 0 };
      cur.taxablePaisa += it.computed.taxableAmountPaisa;
      cur.taxPaisa += it.computed.cgstPaisa + it.computed.sgstPaisa + it.computed.igstPaisa;
      by.set(rate, cur);
    }
    return [...by.entries()].sort((a, b) => a[0] - b[0]);
  }, [items]);
  const globalDiscount = useCartStore((s) => s.globalDiscount);
  const customer = useCartStore((s) => s.customer);
  const appliedPromotionCode = useCartStore((s) => s.appliedPromotionCode);
  const loyaltyPointsRedeemed = useCartStore((s) => s.loyaltyPointsRedeemed);
  const parked = useCartStore((s) => s.parked);
  const updateItemQty = useCartStore((s) => s.updateItemQty);
  const updateItemPrice = useCartStore((s) => s.updateItemPrice);
  const updateItemDiscount = useCartStore((s) => s.updateItemDiscount);
  const removeItem = useCartStore((s) => s.removeItem);
  const setIsInterState = useCartStore((s) => s.setIsInterState);
  const setGlobalDiscount = useCartStore((s) => s.setGlobalDiscount);
  const setAppliedPromotion = useCartStore((s) => s.setAppliedPromotion);
  const setLoyaltyRedemption = useCartStore((s) => s.setLoyaltyRedemption);
  const clearCart = useCartStore((s) => s.clearCart);
  const parkCurrent = useCartStore((s) => s.parkCurrent);
  const recallParked = useCartStore((s) => s.recallParked);
  const discardParked = useCartStore((s) => s.discardParked);
  const addItem = useCartStore((s) => s.addItem);
  const scanFlash = useCartStore((s) => s.scanFlash);

  // Briefly flash the row of a just-scanned product green (paired with the beep).
  const [flashId, setFlashId] = useState<string | null>(null);
  useEffect(() => {
    if (!scanFlash) return;
    setFlashId(scanFlash.productId);
    const t = setTimeout(() => setFlashId(null), 700);
    return () => clearTimeout(t);
  }, [scanFlash]);

  const activeStoreId = useAppStore((s) => s.activeStoreId);

  // Derive place of supply from the customer rather than trusting the cashier to
  // tick a box. A registered customer's GSTIN carries their state code; fall back
  // to the address state. Charging CGST/SGST on an inter-state supply pays the
  // wrong government and the buyer's input credit is at risk.
  //
  // Only fires when the answer is knowable — a walk-in with no GSTIN and no state
  // leaves the operator's choice alone. The checkbox stays live as an override.
  useEffect(() => {
    if (!activeStoreId || !customer) return;
    let alive = true;
    void loadStoreGstIdentity(activeStoreId)
      .then(({ homeStateCode }) => {
        if (!alive) return;
        const derived = resolveInterState(customer, homeStateCode);
        if (derived !== null && derived !== useCartStore.getState().isInterState) {
          setIsInterState(derived);
        }
      })
      .catch(() => { /* leave the operator's choice alone */ });
    return () => { alive = false; };
  }, [activeStoreId, customer, setIsInterState]);

  const [recallConfirmId, setRecallConfirmId] = useState<string | null>(null);
  const [qtyConfirm, setQtyConfirm] = useState<{ productId: string; name: string; qty: number } | null>(null);

  // Apply a quantity change, but confirm first when a line jumps to an unusually
  // high count (from below the threshold).
  function requestQtyChange(item: CartItem, q: number) {
    if (q >= HIGH_QTY_THRESHOLD && item.quantity < HIGH_QTY_THRESHOLD) {
      setQtyConfirm({ productId: item.product.id, name: item.product.name, qty: q });
      return;
    }
    updateItemQty(item.product.id, q);
  }
  // Hoisted: the discount box renders only when the cart has lines, and a hook
  // called inside that branch would unmount itself the moment the cart empties.
  const globalDiscountProps = useNumberDraft({
    value: globalDiscount,
    onCommit: setGlobalDiscount,
    decimals: true,
    blankZero: true,
  });
  const [promoCode, setPromoCode] = useState('');
  const [promoMessage, setPromoMessage] = useState<string | null>(null);
  const [customerPoints, setCustomerPoints] = useState(0);
  const merchantId = useAppStore((s) => s.currentUser?.merchantId);
  const recentProducts = useRecentSoldProducts(activeStoreId, items.length === 0);

  // Total the customer saved this sale (line discounts + global promo/loyalty),
  // surfaced positively above the total.
  const savings =
    totals.globalDiscount + items.reduce((sum, i) => sum + (i.discountAmount || 0), 0);

  // F4 → park. F2 (customer picker) moved to CustomerBar with the control
  // itself — leaving it here too would open two pickers on one keypress.
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === 'F4') {
        e.preventDefault();
        if (items.length > 0) parkCurrent();
      }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [items.length, parkCurrent]);

  const handleRecall = (id: string) => {
    if (items.length > 0) {
      // Confirm: recalling would discard the active cart.
      setRecallConfirmId(id);
    } else {
      recallParked(id);
    }
  };

  useEffect(() => {
    if (!customer?.id) {
      setCustomerPoints(0);
      setLoyaltyRedemption(0, 0);
      return;
    }
    let alive = true;
    database.get<Customer>('customers').find(customer.id).then((row) => {
      if (alive) setCustomerPoints(Math.max(0, row.loyaltyPoints));
    }).catch(() => undefined);
    return () => { alive = false; };
  }, [customer?.id, setLoyaltyRedemption]);

  async function applyPromotion() {
    if (!merchantId) return;
    const result = await validatePromoCode(promoCode, merchantId, totals.finalTotalPaisa);
    if (!result.valid || !result.promotion) {
      setPromoMessage(result.error ?? 'Promotion could not be applied.');
      return;
    }
    setAppliedPromotion(result.promotion.id, result.promotion.code ?? promoCode.trim().toUpperCase(), toRupees(result.discountPaisa));
    setPromoMessage(`Applied ${result.promotion.name}: ${formatCurrency(toRupees(result.discountPaisa))}`);
  }

  return (
    <aside className="flex h-full min-h-0 flex-col rounded-lg border border-stroke-hairline/10 bg-bg-surface">
      {/* No header row: it was a full row tall to show a title, a count and
          two buttons, with most of its width empty. Park/clear moved to the
          action row above the cart (CartActions) and the count moved into the
          totals, so the cart starts straight at the column header and the item
          list gets that height back. */}

      {/* ─── Items list ──────────────────────────────────── */}
      <div className="flex-1 overflow-auto">
        {items.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center gap-5 px-6 text-center">
            <p className="text-body text-fg-tertiary">Search or scan above to add items — or pick a Quick add on the right.</p>
            {recentProducts.length > 0 && (
              <div className="w-full">
                <div className="mb-2 text-label uppercase text-fg-tertiary">Quick add · recently sold</div>
                <div className="flex flex-wrap justify-center gap-2">
                  {recentProducts.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => addItem(toBillingProduct(p))}
                      className="inline-flex items-center gap-1.5 rounded-full border border-stroke-hairline/15 bg-bg-canvas px-3 py-1.5 text-body text-fg-secondary transition-colors hover:border-accent/40 hover:text-fg-primary"
                    >
                      <Plus size={12} className="text-accent-ink" />
                      <span className="max-w-[150px] truncate">{p.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <>
            {/* Column header — orients the eye on a wide cart.
                Tinted (bg-subtle) rather than matching the surface, so the
                header reads as a band separating the cart chrome from the
                items, and stays legible while sticky over scrolling rows. */}
            <div className="sticky top-0 z-10 hidden items-center gap-3 border-y border-stroke-hairline/10 bg-bg-subtle px-4 py-2 sm:flex">
              <span className="min-w-0 flex-[1.6] text-label font-medium uppercase tracking-wide text-fg-secondary">Item</span>
              <span className="min-w-0 flex-1 text-label font-medium uppercase tracking-wide text-fg-secondary">Price</span>
              <span className="w-20 text-right text-label font-medium uppercase tracking-wide text-fg-secondary">Tax</span>
              <span className="w-[104px] text-center text-label font-medium uppercase tracking-wide text-fg-secondary">Qty</span>
              <span className="w-24 text-right text-label font-medium uppercase tracking-wide text-fg-secondary">Amount</span>
              <span className="w-5 shrink-0" aria-hidden />
            </div>
            <ul className="divide-y divide-stroke-hairline/10">
              {items.map((item) => {
                const oversell =
                  item.product.trackInventory &&
                  !item.product.isService &&
                  item.quantity > item.product.currentStock;
                const priceEdited =
                  item.unitPrice !== item.product.sellingPrice || item.discountAmount > 0;
                const belowCost =
                  item.product.purchasePrice != null &&
                  item.product.purchasePrice > 0 &&
                  item.unitPrice < item.product.purchasePrice;
                return (
                  <li
                    key={item.product.id}
                    className={cn(
                      'px-3 py-2.5 transition-colors duration-500 sm:px-4',
                      flashId === item.product.id && 'bg-success/15',
                    )}
                  >
                    {/* Phones: `order` puts name + amount on line one and price, tax,
                        qty on line two; from sm up the row is one line as before. */}
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 sm:flex-nowrap">
                      {/* Item — name + code, and any flags about this LINE.
                       *
                       * "below cost" and the line discount used to live in the
                       * price cell, appended to `₹847.46 × 1 PCS · 18% GST`.
                       * Four separate facts in one run of 12px text, and the
                       * warning was the last of them — so at 1440 it rendered
                       * as "· below cos", clipped mid-word. A warning that can
                       * be truncated is not a warning. They are properties of
                       * the line, so they sit with the line. */}
                      <div className="order-1 flex min-w-0 flex-1 items-baseline gap-1.5 sm:order-none sm:flex-[1.6]">
                        {/* Tap the name for full product details (HSN, GST, stock, margin…). */}
                        <ProductDetailsPopover
                          item={item}
                          trigger={
                            <button
                              type="button"
                              title="View product details"
                              className="min-w-0 truncate text-left text-body font-medium text-fg-primary transition-colors hover:text-accent-ink"
                            >
                              {item.product.name}
                            </button>
                          }
                        />
                        {/* Full tertiary, not /80: the alpha put the SKU at
                            3.25:1, and a code is read character by character. */}
                        {(item.product.sku || item.product.barcode) && (
                          <Code className="shrink-0 text-label uppercase text-fg-tertiary">
                            {item.product.sku || item.product.barcode}
                          </Code>
                        )}

                        {belowCost && (
                          <Explain
                            underline={false}
                            title="Below cost"
                            side="bottom"
                            className="shrink-0 rounded bg-danger/10 px-1.5 py-0.5 text-label font-medium text-danger-ink"
                            content={
                              <>
                                <ExplainRow label="Selling at" value={<Money value={item.unitPrice} />} />
                                <ExplainRow label="Cost price" value={<Money value={item.product.purchasePrice} />} />
                                <ExplainRow
                                  label="Loss per unit"
                                  strong
                                  value={<Money value={item.unitPrice - (item.product.purchasePrice ?? 0)} />}
                                />
                              </>
                            }
                          >
                            Below cost
                          </Explain>
                        )}

                        {item.discountAmount > 0 && (
                          <span className="shrink-0 rounded bg-success/10 px-1.5 py-0.5 text-label font-medium text-success-ink">
                            <Money value={-item.discountAmount} /> off
                          </span>
                        )}
                      </div>

                      {/* Phones only: ends line one (name, amount, remove). */}
                      <span aria-hidden className="order-4 h-0 basis-full sm:hidden" />
                      {/* Price — ₹unit × qty · GST (tap to edit price / discount) */}
                      <div className="order-5 min-w-0 flex-1 overflow-hidden sm:order-none">
                        <LineEditPopover
                          sellingPrice={item.product.sellingPrice}
                          mrp={item.product.mrp}
                          unitPrice={item.unitPrice}
                          discountAmount={item.discountAmount}
                          onPrice={(v) => updateItemPrice(item.product.id, v)}
                          onDiscount={(v) => updateItemDiscount(item.product.id, v)}
                          trigger={
                            <button
                              type="button"
                              title="Tap to edit price / discount"
                              className={cn(
                                'inline-flex max-w-full items-center gap-1.5 truncate text-label tabular-nums transition-colors',
                                priceEdited
                                  ? 'text-accent-ink'
                                  : 'text-fg-tertiary hover:text-fg-primary',
                              )}
                            >
                              {/* One job: the unit price, and that it can be
                                  edited. The quantity is already in its own
                                  column with a stepper, so repeating it as
                                  "× 1" was noise; the unit rides with the
                                  price instead, which is also how Rule 46(f)
                                  reads it — a rate per unit of measure. */}
                              <span className="underline decoration-dashed underline-offset-2">
                                <Money value={item.unitPrice} />
                              </span>
                            </button>
                          }
                        />
                      </div>

                      {/* Tax — its own column, with the working behind it.
                       *
                       * "18% GST" used to be a fragment appended to the price.
                       * It is the answer to a different question, and it is the
                       * one customers argue about, so it gets its own cell and
                       * its evidence: the HSN the rate comes from, and the
                       * CGST/SGST split that will print on the invoice. */}
                      <div className="order-6 w-16 shrink-0 text-right sm:order-none sm:w-20">
                        {item.product.gstRate > 0 ? (
                          <Explain
                            title="Tax on this line"
                            className="text-label text-fg-secondary"
                            content={
                              <>
                                {item.product.hsnCode && (
                                  <ExplainRow label="HSN" value={<Code>{item.product.hsnCode}</Code>} />
                                )}
                                <ExplainRow label="Rate" value={`${item.product.gstRate}%`} />
                                <ExplainRow
                                  label="Taxable"
                                  value={<Money value={item.computed.taxableAmountPaisa / 100} />}
                                />
                                {isInterState ? (
                                  <ExplainRow
                                    label={`IGST ${item.product.gstRate}%`}
                                    strong
                                    value={<Money value={item.computed.igstPaisa / 100} />}
                                  />
                                ) : (
                                  <>
                                    <ExplainRow
                                      label={`CGST ${item.product.gstRate / 2}%`}
                                      value={<Money value={item.computed.cgstPaisa / 100} />}
                                    />
                                    <ExplainRow
                                      label={`SGST ${item.product.gstRate / 2}%`}
                                      value={<Money value={item.computed.sgstPaisa / 100} />}
                                    />
                                  </>
                                )}
                              </>
                            }
                          >
                            {item.product.gstRate}%
                          </Explain>
                        ) : (
                          <span className="text-label text-fg-tertiary">—</span>
                        )}
                      </div>

                      {/* Qty stepper, with the unit.
                       *
                       * The unit rides with the QUANTITY, not the price. "1"
                       * on its own is ambiguous — 1 kg and 1 pcs are very
                       * different sales — and it is a Rule 46(f) field on the
                       * invoice for goods, so the operator should see what the
                       * bill will state. Splitting the old combined cell moved
                       * it off screen for a moment; this is where it belongs. */}
                      <div data-testid="cart-qty" className="order-7 flex shrink-0 items-center gap-1.5 sm:order-none">
                        <QtyStepper value={item.quantity} onChange={(q) => requestQtyChange(item, q)} />
                        {item.product.unit && (
                          <span className="w-9 shrink-0 text-label uppercase text-fg-tertiary">
                            {item.product.unit}
                          </span>
                        )}
                      </div>

                      {/* Line total */}
                      <span className="order-2 shrink-0 text-right text-body font-semibold text-fg-primary sm:order-none sm:w-24">
                        <Money value={item.computed.totalPaisa / 100} />
                      </span>

                      {/* Remove */}
                      <button
                        type="button"
                        onClick={() => removeItem(item.product.id)}
                        className="order-3 w-5 shrink-0 text-fg-tertiary transition-colors hover:text-danger-ink sm:order-none"
                        aria-label="Remove item"
                      >
                        <X size={14} />
                      </button>
                    </div>

                    {/* Oversell guard — flag when cart qty exceeds what's in stock. */}
                    {oversell && (
                      <div className="mt-1 text-label text-danger-ink">
                        Only {item.product.currentStock} in stock — exceeds available
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </div>

      {/* ─── Totals + actions ──────────────────────────────
          Tinted to match the column header, so the cart reads as three bands:
          tinted header, white item list, tinted summary. The white list is
          then the only bright area, which is where the eye should go while
          ringing up. */}
      {items.length > 0 && (
        <div className="border-t border-stroke-hairline/10 bg-bg-subtle px-3 py-2.5 sm:px-4">
          <div className="grid grid-cols-1 gap-x-5 gap-y-2 sm:grid-cols-2">
            {/* Left — adjustments (tax, discount, promo, loyalty) */}
            <div className="space-y-2">
              <label
                className="flex items-center justify-between text-body text-fg-secondary cursor-pointer"
                title={customer
                  ? "Set from the customer's GSTIN or state. Override if needed."
                  : 'Tick for an out-of-state supply.'}
              >
                <span>Inter-state (IGST)</span>
                <input
                  type="checkbox"
                  checked={isInterState}
                  onChange={(e) => setIsInterState(e.target.checked)}
                  className="h-4 w-4 rounded border-stroke-hairline/30"
                />
              </label>

              <div className="flex items-center justify-between gap-2">
                <span className="text-body text-fg-secondary">Discount</span>
                <Input
                  {...globalDiscountProps}
                  placeholder="0.00"
                  className="h-8 w-28 text-right tabular-nums"
                />
              </div>

              <div className="rounded-md border border-stroke-hairline/10 bg-bg-canvas p-2 space-y-1.5">
                <div className="flex gap-2">
                  <Input
                    value={promoCode}
                    onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                    placeholder="Promo code"
                    className="h-8"
                  />
                  <Button variant="secondary" size="sm" onClick={() => void applyPromotion()}>Apply</Button>
                </div>
                {appliedPromotionCode && (
                  <button
                    type="button"
                    onClick={() => { setAppliedPromotion(null, null, 0); setPromoCode(''); setPromoMessage(null); }}
                    className="text-label uppercase text-accent-ink hover:underline"
                  >
                    {appliedPromotionCode} applied · remove
                  </button>
                )}
                {promoMessage && <div className="text-label text-fg-tertiary">{promoMessage}</div>}
                {customer && customerPoints > 0 && (
                  <div className="flex items-center justify-between gap-2 border-t border-stroke-hairline/10 pt-2">
                    <span className="text-label text-fg-secondary tabular-nums">{customerPoints} loyalty points</span>
                    {loyaltyPointsRedeemed > 0 ? (
                      <button className="text-label uppercase text-accent-ink" onClick={() => setLoyaltyRedemption(0, 0)}>Remove redemption</button>
                    ) : (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => {
                          // Points are not rupees. They were treated as though
                          // they were — redeem(n, n) — which only looked right
                          // because the rate happened to be 1:1.
                          const cfg = getLoyaltyConfig();
                          const redeem = maxRedeemablePoints(customerPoints, totals.totalAmount, cfg);
                          setLoyaltyRedemption(redeem, loyaltyRedemptionValue(redeem, cfg));
                        }}
                      >
                        Redeem
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Right — money summary */}
            <div className="flex flex-col justify-end space-y-1.5">
              <Row
                label={`Subtotal · ${itemCount} ${itemCount === 1 ? 'item' : 'items'}`}
                value={<Money value={totals.subtotal} />}
              />
              {totals.globalDiscount > 0 && (
                <Row label="Global discount" value={<Money value={-totals.globalDiscount} />} muted />
              )}
              {totals.cgst + totals.sgst > 0 && (
                <Row
                  muted
                  label={
                    <Explain
                      title="Tax by rate"
                      side="left"
                      className="text-body text-fg-secondary"
                      content={
                        <>
                          {taxSlabs.map(([rate, s]) => (
                            <ExplainRow
                              key={rate}
                              label={`${rate}% on ${formatCurrency(s.taxablePaisa / 100)}`}
                              value={<Money value={s.taxPaisa / 100} />}
                            />
                          ))}
                          <div className="!mt-2 border-t border-stroke-hairline/15 pt-1.5">
                            <ExplainRow
                              label="Total tax"
                              strong
                              value={<Money value={totals.cgst + totals.sgst + totals.igst} />}
                            />
                          </div>
                        </>
                      }
                    >
                      CGST + SGST
                    </Explain>
                  }
                  value={<Money value={totals.cgst + totals.sgst} />}
                />
              )}
              {totals.igst > 0 && <Row label="IGST" value={<Money value={totals.igst} />} muted />}
              {totals.cess > 0 && <Row label="Cess" value={<Money value={totals.cess} />} muted />}
              {totals.roundOff !== 0 && (
                <Row
                  muted
                  label={
                    <Explain
                      title="Round off"
                      side="left"
                      className="text-body text-fg-secondary"
                      content={
                        <>
                          <ExplainRow
                            label="Before rounding"
                            value={<Money value={totals.totalAmount - totals.roundOff} />}
                          />
                          <ExplainRow label="Adjustment" value={<Money value={totals.roundOff} signed />} />
                          <ExplainRow label="Charged" strong value={<Money value={totals.totalAmount} />} />
                          <p className="!mt-2 text-label text-fg-tertiary">
                            Rounded to the nearest rupee, per s.170 of the CGST Act.
                          </p>
                        </>
                      }
                    >
                      Round off
                    </Explain>
                  }
                  value={<Money value={totals.roundOff} signed />}
                />
              )}
              {savings > 0 && (
                <div className="flex items-center justify-between rounded-md bg-success/10 px-2.5 py-1.5 text-body">
                  <span className="font-medium text-success-ink">You saved</span>
                  <Money value={savings} className="font-semibold text-success-ink" />
                </div>
              )}
              <div className="flex items-baseline justify-between border-t border-stroke-hairline/10 pt-2">
                <span className="text-heading text-fg-primary">Total</span>
                {/* `text-total` (24px), not `text-display`: the brand puts
                    totals at 18–24px, and naming it after the job means the
                    cart and the checkout can never drift apart. */}
                <Money value={totals.totalAmount} size="total" className="text-fg-primary" />
              </div>
            </div>
          </div>

          <Button block size="lg" className="mt-2.5" onClick={onCharge}>
            Charge {formatCurrency(totals.totalAmount)}  ·  F9
          </Button>
        </div>
      )}

      {/* High-quantity confirm */}
      <Dialog open={!!qtyConfirm} onOpenChange={(o) => !o && setQtyConfirm(null)}>
        <DialogContent title="Confirm quantity" className="w-[380px]">
          <div className="space-y-4 px-5 py-4">
            <p className="text-body text-fg-secondary">
              Set{' '}
              <span className="font-semibold tabular-nums text-fg-primary">{qtyConfirm?.qty}</span> ×{' '}
              <span className="font-medium text-fg-primary">{qtyConfirm?.name}</span>? That’s a large
              quantity — confirm it’s correct.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setQtyConfirm(null)}>
                Cancel
              </Button>
              <Button
                onClick={() => {
                  if (qtyConfirm) updateItemQty(qtyConfirm.productId, qtyConfirm.qty);
                  setQtyConfirm(null);
                }}
              >
                Yes, set {qtyConfirm?.qty}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Recall-confirm dialog (only shown when active cart has items) */}
      <Dialog open={!!recallConfirmId} onOpenChange={(o) => !o && setRecallConfirmId(null)}>
        <DialogContent title="Replace current cart?" className="w-[420px]">
          <div className="space-y-4">
            <p className="text-body text-fg-secondary">
              Recalling a parked cart will replace the active cart. Park the current cart
              first if you want to keep it.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setRecallConfirmId(null)}>
                Cancel
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  parkCurrent();
                  if (recallConfirmId) recallParked(recallConfirmId);
                  setRecallConfirmId(null);
                }}
              >
                Park &amp; recall
              </Button>
              <Button
                onClick={() => {
                  if (recallConfirmId) recallParked(recallConfirmId);
                  setRecallConfirmId(null);
                }}
              >
                Discard &amp; recall
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </aside>
  );
}

// ═════════════════════════════════════════════════════════════════════
//  Pieces
// ═════════════════════════════════════════════════════════════════════

function ParkedCartsMenu({
  parked,
  onRecall,
  onDiscard,
}: {
  parked: ReturnType<typeof useCartStore.getState>['parked'];
  onRecall: (id: string) => void;
  onDiscard: (id: string) => void;
}) {
  if (parked.length === 0) return null;
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1 rounded-md border border-stroke-hairline/15 bg-bg-canvas px-2 py-1.5 text-body text-fg-secondary hover:border-accent/40 hover:text-fg-primary transition-colors"
        >
          <History size={14} /> Held
          <span className="ml-0.5 rounded-full bg-accent/15 px-1.5 text-label uppercase text-accent-ink tabular-nums">
            {parked.length}
          </span>
          <ChevronDown size={12} />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={6}
          className="z-50 w-72 rounded-md border border-stroke-hairline/15 bg-bg-surface p-1 shadow-e2"
        >
          {parked.map((slot) => (
            <DropdownMenu.Item
              key={slot.id}
              onSelect={() => onRecall(slot.id)}
              className="flex cursor-pointer items-center justify-between gap-2 rounded-sm px-2.5 py-2 outline-none data-[highlighted]:bg-bg-subtle"
            >
              <div className="min-w-0 flex-1">
                <div className="truncate text-body text-fg-primary">{slot.label}</div>
                <div className="text-label uppercase text-fg-tertiary tabular-nums">
                  {slot.itemCount} {slot.itemCount === 1 ? 'item' : 'items'} ·{' '}
                  {formatCurrency(slot.totalAmount)} ·{' '}
                  {timeAgo(slot.parkedAt)}
                </div>
              </div>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onDiscard(slot.id);
                }}
                aria-label="Discard parked cart"
                className="rounded p-1 text-fg-tertiary hover:bg-danger/10 hover:text-danger-ink transition-colors"
              >
                <Trash2 size={12} />
              </button>
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

function LineEditPopover({
  sellingPrice,
  mrp,
  unitPrice,
  discountAmount,
  onPrice,
  onDiscount,
  trigger,
}: {
  sellingPrice: number;
  mrp: number;
  unitPrice: number;
  discountAmount: number;
  onPrice: (v: number) => void;
  onDiscount: (v: number) => void;
  trigger: React.ReactNode;
}) {
  const dirty = unitPrice !== sellingPrice || discountAmount > 0;
  const unitPriceProps = useNumberDraft({ value: unitPrice, onCommit: onPrice, decimals: true });
  const discountProps = useNumberDraft({
    value: discountAmount,
    onCommit: onDiscount,
    decimals: true,
    blankZero: true,
  });

  return (
    <Popover.Root>
      <Popover.Trigger asChild>{trigger}</Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          side="left"
          align="start"
          sideOffset={6}
          className="z-50 w-64 rounded-md border border-stroke-hairline/15 bg-bg-surface p-3 shadow-e2"
        >
          <div className="space-y-2.5">
            <Field
              label="Unit price"
              hint={mrp > 0 ? `MRP ${formatCurrency(mrp)} · List ${formatCurrency(sellingPrice)}` : `List ${formatCurrency(sellingPrice)}`}
            >
              <Input
                {...unitPriceProps}
                className="h-8 text-right tabular-nums"
              />
            </Field>
            <Field label="Discount (₹)">
              <Input
                {...discountProps}
                placeholder="0.00"
                className="h-8 text-right tabular-nums"
              />
            </Field>
            {dirty && (
              <button
                type="button"
                onClick={() => {
                  onPrice(sellingPrice);
                  onDiscount(0);
                }}
                className="text-label uppercase text-fg-tertiary hover:text-fg-primary transition-colors"
              >
                Reset to list price
              </button>
            )}
          </div>
          <Popover.Arrow className="fill-bg-surface" />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

/** Read-only product details for a cart line — tap the name to see everything
 *  that matters at billing: HSN, GST, MRP/prices, stock, margin, and the live
 *  per-line tax breakdown. */
function ProductDetailsPopover({ item, trigger }: { item: CartItem; trigger: React.ReactNode }) {
  const p = item.product;
  const c = item.computed;
  const marginPct =
    p.purchasePrice != null && p.purchasePrice > 0 && item.unitPrice > 0
      ? ((item.unitPrice - p.purchasePrice) / item.unitPrice) * 100
      : null;
  const interState = c.igstPaisa > 0;
  return (
    <Popover.Root>
      <Popover.Trigger asChild>{trigger}</Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          side="left"
          align="start"
          sideOffset={6}
          className="z-50 w-72 rounded-md border border-stroke-hairline/15 bg-bg-surface p-3 shadow-e2"
        >
          <div className="mb-2">
            <div className="text-body font-medium text-fg-primary">{p.name}</div>
            {(p.sku || p.barcode) && (
              <div className="text-label uppercase text-fg-tertiary tabular-nums">{p.sku || p.barcode}</div>
            )}
          </div>

          <div className="space-y-1.5">
            <DetailRow label="HSN / SAC" value={p.hsnCode || '—'} />
            <DetailRow label="GST" value={`${p.gstRate}%`} />
            {p.cessRate > 0 && <DetailRow label="Cess" value={`${p.cessRate}%`} />}
            <DetailRow label="MRP" value={p.mrp > 0 ? formatCurrency(p.mrp) : '—'} />
            <DetailRow label="List price" value={formatCurrency(p.sellingPrice)} />
            {item.unitPrice !== p.sellingPrice && (
              <DetailRow label="Unit price" value={formatCurrency(item.unitPrice)} accent />
            )}
            {item.discountAmount > 0 && (
              <DetailRow label="Line discount" value={`− ${formatCurrency(item.discountAmount)}`} />
            )}
            <DetailRow label="Unit" value={p.unit || '—'} />
            {p.trackInventory && !p.isService && (
              <DetailRow label="In stock" value={`${p.currentStock}${p.unit ? ` ${p.unit}` : ''}`} />
            )}
            {marginPct != null && <DetailRow label="Margin" value={`${marginPct.toFixed(1)}%`} muted />}
          </div>

          {/* Live per-line tax breakdown for this quantity. */}
          <div className="mt-2 space-y-1.5 border-t border-stroke-hairline/10 pt-2">
            <DetailRow label={`Taxable · qty ${item.quantity}`} value={formatCurrency(c.taxableAmountPaisa / 100)} muted />
            {interState ? (
              <DetailRow label={`IGST ${c.gstRate}%`} value={formatCurrency(c.igstPaisa / 100)} muted />
            ) : (
              <>
                <DetailRow label={`CGST ${c.gstRate / 2}%`} value={formatCurrency(c.cgstPaisa / 100)} muted />
                <DetailRow label={`SGST ${c.gstRate / 2}%`} value={formatCurrency(c.sgstPaisa / 100)} muted />
              </>
            )}
            {c.cessPaisa > 0 && <DetailRow label="Cess" value={formatCurrency(c.cessPaisa / 100)} muted />}
            <div className="flex items-center justify-between pt-0.5">
              <span className="text-body font-medium text-fg-primary">Line total</span>
              <span className="text-body font-semibold text-fg-primary tabular-nums">
                {formatCurrency(c.totalPaisa / 100)}
              </span>
            </div>
          </div>
          <Popover.Arrow className="fill-bg-surface" />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

function DetailRow({
  label,
  value,
  accent,
  muted,
}: {
  label: string;
  value: string;
  accent?: boolean;
  muted?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-3 text-body">
      <span className="text-fg-secondary">{label}</span>
      <span
        className={cn(
          'tabular-nums',
          accent ? 'font-medium text-accent-ink' : muted ? 'text-fg-secondary' : 'text-fg-primary',
        )}
      >
        {value}
      </span>
    </div>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-label uppercase text-fg-tertiary">{label}</span>
      {children}
      {hint && <span className="text-label text-fg-tertiary tabular-nums">{hint}</span>}
    </label>
  );
}

function QtyStepper({ value, onChange }: { value: number; onChange: (n: number) => void }) {
  return (
    <div className="inline-flex items-center rounded-md border border-stroke-hairline/15 bg-bg-canvas">
      <button
        type="button"
        // Stops at 1. Stepping down used to reach 0, which the cart store reads
        // as "remove", so a held key quietly dropped the line. Taking an item
        // off the bill is the bin button's job, where it is deliberate.
        onClick={() => onChange(Math.max(1, value - 1))}
        disabled={value <= 1}
        className="flex h-7 w-7 items-center justify-center text-fg-secondary hover:bg-bg-subtle hover:text-fg-primary disabled:opacity-30 transition-colors"
        aria-label="Decrease"
      >
        <Minus size={12} />
      </button>
      {/* commitOn="blur" so a quantity is one decision: typing "20" must not
          pass through 2 on its way, or the high-quantity confirm ends up
          asking about 20 while 2 has already been applied to the bill. */}
      <NumberField
        aria-label="Quantity"
        value={value}
        onCommit={onChange}
        commitOn="blur"
        min={1}
        className="h-7 w-12 bg-transparent text-center text-body text-fg-primary outline-none"
      />
      <button
        type="button"
        onClick={() => onChange(value + 1)}
        className="flex h-7 w-7 items-center justify-center text-fg-secondary hover:bg-bg-subtle hover:text-fg-primary transition-colors"
        aria-label="Increase"
      >
        <Plus size={12} />
      </button>
    </div>
  );
}

function Row({ label, value, muted }: { label: ReactNode; value: ReactNode; muted?: boolean }) {
  return (
    // A muted row is still a tax line on a bill, so `muted` only steps the
    // *amount* down to secondary; the label stays put. It used to drop the
    // label to tertiary, which measured 4.34:1 against the totals panel, and
    // "CGST + SGST" is not decoration.
    <div className="flex items-center justify-between text-body">
      <span className="text-fg-secondary">{label}</span>
      <span className={muted ? 'text-fg-secondary' : 'text-fg-primary'}>{value}</span>
    </div>
  );
}

/**
 * Recently-sold products for the active store, most-recent first, deduped —
 * powers the empty-cart "quick add" chips. Loads only while `enabled` (cart is
 * empty) so it never runs mid-sale.
 */
function useRecentSoldProducts(storeId: string | null, enabled: boolean, limit = 6): Product[] {
  const [products, setProducts] = useState<Product[]>([]);
  useEffect(() => {
    if (!storeId || !enabled) {
      setProducts([]);
      return;
    }
    let alive = true;
    (async () => {
      try {
        const invoices = await database
          .get<Invoice>('invoices')
          .query(
            Q.where('store_id', storeId),
            Q.where('is_deleted', false),
            Q.where('type', 'SALE'),
            Q.sortBy('created_at', Q.desc),
            Q.take(20),
          )
          .fetch();
        if (invoices.length === 0) {
          if (alive) setProducts([]);
          return;
        }
        const order = new Map(invoices.map((inv, i) => [inv.id, i]));
        const lines = await database
          .get<LineItem>('line_items')
          .query(
            Q.where('invoice_id', Q.oneOf(invoices.map((i) => i.id))),
            Q.where('is_deleted', false),
          )
          .fetch();
        lines.sort((a, b) => (order.get(a.invoiceId) ?? 1e9) - (order.get(b.invoiceId) ?? 1e9));
        const ids: string[] = [];
        const seen = new Set<string>();
        for (const l of lines) {
          if (!seen.has(l.productId)) {
            seen.add(l.productId);
            ids.push(l.productId);
          }
          if (ids.length >= limit * 2) break;
        }
        if (ids.length === 0) {
          if (alive) setProducts([]);
          return;
        }
        const prods = await database
          .get<Product>('products')
          .query(
            Q.where('id', Q.oneOf(ids)),
            Q.where('is_deleted', false),
            Q.where('is_active', true),
          )
          .fetch();
        const byId = new Map(prods.map((p) => [p.id, p]));
        const result = ids
          .map((id) => byId.get(id))
          .filter((p): p is Product => !!p)
          .slice(0, limit);
        if (alive) setProducts(result);
      } catch {
        if (alive) setProducts([]);
      }
    })();
    return () => {
      alive = false;
    };
  }, [storeId, enabled, limit]);
  return products;
}

/** Compact "5m ago" / "2h ago" formatter for parked-cart timestamps. */
function timeAgo(ts: number): string {
  const diff = Math.max(0, Date.now() - ts);
  const m = Math.floor(diff / 60_000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}
